import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Group,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { ChevronDown, ChevronUp, Edit2 } from 'react-feather';
import { ContentCard } from '../../../components/common/ContentCard';
import type {
  ReflectionQuestionWithResponse,
  ReflectionStatus,
} from '../../../services/api/preSessionReflection';

interface Props {
  questions: ReflectionQuestionWithResponse[];
  status: ReflectionStatus;
  estimatedMinutes: number;
  isLocked: boolean;
  /** Called on every keystroke (debounced upstream by useReflectionAutosave). */
  onChange: (questionId: string, responseText: string) => void;
  /** Called when the participant clicks the explicit Save button. */
  onSaveAll: (
    pending: Array<{ question_id: string; response_text: string }>
  ) => Promise<void>;
  /** "Saving…", "Saved", "Save failed — try again", etc. */
  saveHint?: string;
}

const TITLE_COLOR = '#1A1A2E';

const STATUS_LABELS: Record<ReflectionStatus, string> = {
  NOT_STARTED: 'NOT STARTED',
  IN_PROGRESS: 'IN PROGRESS',
  COMPLETED: 'COMPLETED',
};

const STATUS_COLORS: Record<ReflectionStatus, string> = {
  NOT_STARTED: 'gray',
  IN_PROGRESS: 'warningAmber',
  COMPLETED: 'medleyGreen',
};

/**
 * Collapsible reflection form (MED-803 AC #5 / #6 / #7 / #8 / #9).
 *
 * MED-1113 — built on the shared `ContentCard` (elevated, radius 16) to match
 * the other prep cards, with the status pill rendered in the canonical Session
 * Prep "grail" style (mirroring `PrepStepCard`).
 *
 *   - Status pill: NOT STARTED / IN PROGRESS / COMPLETED (gray / amber / green).
 *   - Right-aligned subhead: "{N} questions · about {X} minutes".
 *   - Anonymous-attribution intro copy per AC #6 (NOT "Sarah will read your
 *     responses" — that mock language is explicitly forbidden).
 *   - Each question is a textarea with the configured placeholder.
 *
 * MED-1113 (AC4 / AC5) — collapse trigger fix. The card seeds its collapsed
 * state from completion on mount (a returning, already-COMPLETED reflection
 * loads collapsed with no flash) and collapses on an explicit Save once every
 * question is answered. It does NOT auto-collapse while the participant is
 * typing (the prior `allAnswered && COMPLETED` effect closed the tray mid-edit
 * — the UAT bug). The reflection stays fully editable up to session start
 * (Option B): re-expanding keeps the editable form, no read-only lock.
 */
export function ReflectionFormCard({
  questions,
  status,
  estimatedMinutes,
  isLocked,
  onChange,
  onSaveAll,
  saveHint,
}: Props) {
  const initialValues = useMemo(
    () => Object.fromEntries(questions.map(q => [q.id, q.response_text])),
    [questions]
  );
  const [values, setValues] = useState<Record<string, string>>(initialValues);
  // MED-1113 (AC4) — seed collapsed from completion so an already-complete
  // reflection loads collapsed on a return visit without flashing open. This is
  // the ONLY load-time collapse; thereafter collapse is driven solely by Save
  // and the manual chevron — never by typing.
  const [collapsed, setCollapsed] = useState(() => status === 'COMPLETED');
  const [savingNow, setSavingNow] = useState(false);

  // When the upstream payload changes (refetch), sync local state.
  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const handleChange = (questionId: string, text: string) => {
    setValues(prev => ({ ...prev, [questionId]: text }));
    onChange(questionId, text);
  };

  const handleSave = async () => {
    const pending = questions.map(q => ({
      question_id: q.id,
      response_text: values[q.id] ?? '',
    }));
    setSavingNow(true);
    try {
      await onSaveAll(pending);
      // MED-1113 (AC4) — collapse on explicit Save, but only once complete
      // (every question answered). Incomplete saves stay expanded.
      const allAnswered =
        questions.length > 0 &&
        questions.every(q => (values[q.id] ?? '').trim().length > 0);
      if (allAnswered) setCollapsed(true);
    } finally {
      setSavingNow(false);
    }
  };

  // Shared form body — the intro copy, the question textareas, and the Save
  // row. Both the standalone card and the embedded (peer-step) variant render
  // this; only the surrounding chrome differs.
  const formBody = (
    <Stack gap="md">
      <Text size="sm">
        Take a few minutes to reflect before your session. Your responses are
        shared anonymously with your group leader to shape the conversation.
      </Text>

      {questions.length === 0 ? (
        <Text size="sm" c="dimmed" fs="italic">
          No reflection questions have been configured for this session yet.
        </Text>
      ) : (
        questions.map(q => (
          <Stack key={q.id} gap={4}>
            <Text component="label" htmlFor={`q-${q.id}`} fw={600} size="sm">
              {q.prompt}
            </Text>
            <Textarea
              id={`q-${q.id}`}
              placeholder={q.placeholder_text ?? ''}
              value={values[q.id] ?? ''}
              onChange={e => handleChange(q.id, e.currentTarget.value)}
              autosize
              minRows={3}
              disabled={isLocked}
            />
          </Stack>
        ))
      )}

      {!isLocked && (
        <Group justify="space-between" align="center">
          <Box>
            {saveHint && (
              <Text size="xs" c="dimmed" aria-live="polite">
                {saveHint}
              </Text>
            )}
          </Box>
          <Button
            onClick={handleSave}
            loading={savingNow}
            variant="filled"
            color="dark"
          >
            Save
          </Button>
        </Group>
      )}
    </Stack>
  );

  return (
    // MED-1113 — standalone (participant page): page-controlled-style collapse
    // on the shared ContentCard shell (matching PrepStepCard): the full header
    // lives in the header slot with the chevron toggle on the right,
    // contentGap=0 keeps the card tight to its header when collapsed, and the
    // body sits inside a controlled Collapse.
    <ContentCard
      variant="elevated"
      radius={16}
      padding="lg"
      contentGap={0}
      fill={false}
      header={
        <Group justify="space-between" align="center" wrap="wrap">
          <Group gap="xs" align="center">
            <Edit2 size={18} color="#6B7280" aria-hidden />
            <Text fw={600} fz={17} c={TITLE_COLOR}>
              Your Pre-Session Reflection
            </Text>
            <StatusPill status={status} />
          </Group>
          <Group gap="md" align="center">
            <Text size="sm" c="dimmed">
              {questions.length}{' '}
              {questions.length === 1 ? 'question' : 'questions'} · about{' '}
              {estimatedMinutes} minutes
            </Text>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="md"
              onClick={() => setCollapsed(c => !c)}
              aria-label={
                collapsed ? 'Expand reflection' : 'Collapse reflection'
              }
              aria-expanded={!collapsed}
            >
              {collapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </ActionIcon>
          </Group>
        </Group>
      }
    >
      <Collapse in={!collapsed}>
        <Box mt="md">{formBody}</Box>
      </Collapse>
    </ContentCard>
  );
}

function StatusPill({ status }: { status: ReflectionStatus }) {
  // MED-1113 — canonical Session Prep "grail" badge (matching PrepStepCard / the
  // coach agenda pill): radius xl, 12px/600, +1px nudge, 0.6 tracking. Light for
  // NOT STARTED, filled for the active/done states.
  return (
    <Badge
      color={STATUS_COLORS[status]}
      variant={status === 'NOT_STARTED' ? 'light' : 'filled'}
      radius="xl"
      tt="uppercase"
      styles={{
        root: { letterSpacing: '0.6px' },
        label: {
          fontSize: 12,
          fontWeight: 600,
          transform: 'translateY(1px)',
        },
      }}
    >
      {STATUS_LABELS[status]}
    </Badge>
  );
}
